import React from 'react';
import { WxbIcon } from '../Icon/Icon';
import type { WxbIconName } from '../Icon/icons';
import './ChoiceCard.css';

export interface WxbChoiceCardOption {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  icon?: WxbIconName | React.ReactNode;
  badge?: React.ReactNode;
  disabled?: boolean;
}

export interface WxbChoiceCardGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  options: WxbChoiceCardOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  columns?: 1 | 2 | 3 | 'auto';
  size?: 'sm' | 'md';
  disabled?: boolean;
  name?: string;
}

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

const firstEnabledValue = (options: WxbChoiceCardOption[]) => (
  options.find((option) => !option.disabled)?.value ?? ''
);

const renderIcon = (icon: WxbChoiceCardOption['icon']) => {
  if (!icon) return null;
  if (typeof icon === 'string') {
    return <WxbIcon name={icon} size={20} />;
  }
  return icon;
};

export const WxbChoiceCardGroup: React.FC<WxbChoiceCardGroupProps> = ({
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  columns = 'auto',
  size = 'md',
  disabled = false,
  name,
  className = '',
  'aria-label': ariaLabel = '选项',
  ...props
}) => {
  const autoName = React.useId();
  const radioName = name ?? autoName;
  const initialValue = options.some((option) => option.value === defaultValue && !option.disabled)
    ? defaultValue!
    : firstEnabledValue(options);
  const [internalValue, setInternalValue] = React.useState(initialValue);
  const requestedValue = controlledValue ?? internalValue;
  const currentValue = options.some((option) => option.value === requestedValue && !option.disabled)
    ? requestedValue
    : firstEnabledValue(options);

  React.useEffect(() => {
    if (controlledValue !== undefined || internalValue === currentValue) return;
    setInternalValue(currentValue);
  }, [controlledValue, currentValue, internalValue]);

  const select = (option: WxbChoiceCardOption) => {
    if (disabled || option.disabled) return;
    if (controlledValue === undefined) setInternalValue(option.value);
    onChange?.(option.value);
  };

  return (
    <div
      {...props}
      className={joinClasses(
        'wxb-choice-card-group',
        `wxb-choice-card-group-${columns}`,
        `wxb-choice-card-${size}`,
        className,
      )}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
    >
      {options.map((option) => {
        const checked = currentValue === option.value;
        const optionDisabled = disabled || option.disabled;

        return (
          <label
            key={option.value}
            className={joinClasses(
              'wxb-choice-card',
              checked && 'is-checked',
              optionDisabled && 'is-disabled',
            )}
          >
            <input
              className="wxb-choice-card-input"
              type="radio"
              name={radioName}
              value={option.value}
              checked={checked}
              disabled={optionDisabled}
              onChange={() => select(option)}
            />
            <span className="wxb-choice-card-check" aria-hidden="true" />
            {option.icon && <span className="wxb-choice-card-icon" aria-hidden="true">{renderIcon(option.icon)}</span>}
            <span className="wxb-choice-card-copy">
              <span className="wxb-choice-card-label-row">
                <span className="wxb-choice-card-label">{option.label}</span>
                {option.badge && <span className="wxb-choice-card-badge">{option.badge}</span>}
              </span>
              {option.description && <span className="wxb-choice-card-description">{option.description}</span>}
            </span>
          </label>
        );
      })}
    </div>
  );
};
